import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { usePlanTracker, PLANS } from './hooks/usePlanTracker';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './PricingPage.css';

const STRIPE_PAYMENT_LINKS = {
  PAID:     'https://buy.stripe.com/test_XXXXXXXXXXXXX',
  LIFETIME: 'https://buy.stripe.com/test_ZZZZZZZZZZZZZ',
};

// Full plan configuration for the UI
const pricingPlans = [
  {
    ...PLANS.PAID,
    key: 'PAID',
    name: 'Single Filing',
    price: 50,
    period: 'submission',
    description: 'One-time filing, any tool — Form 5472, BOI Report, EIN Application, or ITIN Package.',
    features: [
      '1 filing, any of our 4 tools',
      'Form 5472 · BOI Report · EIN Application · ITIN Package',
      'Bank statement parsing',
      'Official IRS-ready PDF',
      'Direct fax to IRS (where applicable)',
      'Fax delivery confirmation',
      'Email support',
    ],
    cta: 'Purchase for $50',
    paymentLink: STRIPE_PAYMENT_LINKS.PAID,
    popular: true,
  },
  {
    ...PLANS.LIFETIME,
    key: 'LIFETIME',
    name: 'Lifetime Unlimited',
    price: 399,
    period: 'lifetime',
    description: 'Unlimited filings across all 4 tools, forever. Best for accountants and frequent filers.',
    features: [
      'Unlimited filings, all 4 tools',
      'Form 5472 · BOI Report · EIN Application · ITIN Package',
      'Bank statement parsing',
      'Official IRS-ready PDFs',
      'Direct fax to IRS (where applicable)',
      'Fax delivery confirmation',
      'Priority email support',
      'No per-filing fees ever',
    ],
    cta: 'Get Lifetime Access',
    paymentLink: STRIPE_PAYMENT_LINKS.LIFETIME,
    popular: false,
  },
];

function CheckIcon({ color = 'var(--green)' }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
      <polyline points="20 6 9 17 4 12" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`pp-faq-item ${open ? 'open' : ''}`} onClick={() => setOpen(!open)}>
      <div className="pp-faq-question">
        <span>{question}</span>
        <svg className="pp-faq-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
          <polyline points="6 9 12 15 18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      <div className="pp-faq-answer"><p>{answer}</p></div>
    </div>
  );
}

const FAQS = [
  {
    question: 'What exactly do I get for $50?',
    answer: 'One complete filing on any of our 4 tools — Form 5472, BOI Report, EIN Application, or ITIN Package. We parse your bank statement where needed, fill the form, and fax it directly to the IRS. You get a download of the filed PDF and a fax delivery confirmation.',
  },
  {
    question: 'Who is the Lifetime plan for?',
    answer: 'Accountants, tax professionals, or founders with multiple LLCs. Pay once, file as many Form 5472s, BOI Reports, EIN Applications, or ITIN Packages as you need, forever. At $399 it pays for itself after 8 filings.',
  },
  {
    question: 'Does the $50 filing work for any of the 4 tools?',
    answer: 'Yes — one $50 purchase covers a single filing on whichever tool you use: Form 5472, BOI Report, EIN Application, or ITIN Package. Need another filing after that? Purchase again for $50, or upgrade to Lifetime.',
  },
  {
    question: 'What if I already filed and need to file next year?',
    answer: 'Just purchase another single filing for $50. Or upgrade to Lifetime now and never think about it again.',
  },
  {
    question: 'Is this accepted by the IRS?',
    answer: 'Yes. We use the official IRS forms, filled to IRS spec, and fax to the official IRS fax number where a fax filing is required.',
  },
  {
    question: 'What is the penalty for not filing?',
    answer: 'The IRS imposes a $25,000 penalty per year for foreign-owned LLCs that miss Form 5472. Filing on time eliminates that risk entirely.',
  },
  {
    question: 'Can I get a refund?',
    answer: 'If the fax fails to deliver and we cannot resend successfully, yes — full refund. If the form was successfully delivered to the IRS, the service has been rendered.',
  },
];

export default function PricingPage() {
  const { user, authLoading } = useAuth();
  const navigate = useNavigate();
  const planTracker = usePlanTracker(user?.uid);
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const currentPlan = planTracker?.currentPlan || null;
  const planLoading = planTracker?.loading ?? true;
  const usageStats = user && planTracker?.getUsageStats 
    ? planTracker.getUsageStats() 
    : null;

  useEffect(() => {
    const ok = searchParams.get('success');
    const plan = searchParams.get('plan');
    if (ok === 'true' && plan) {
      setSuccess(`Payment successful! Your ${plan.toUpperCase()} plan is now active.`);
      setTimeout(() => window.history.replaceState({}, '', window.location.pathname), 4000);
    }
  }, [searchParams]);

  const handleSelect = (plan) => {
    setError(null);
    setSuccess(null);

    if (!user) {
      navigate('/login');
      return;
    }

    // Already on this exact plan
    if (currentPlan === plan.key) {
      setError(`You already have the ${plan.name} plan.`);
      return;
    }

    // Lifetime users can't buy anything else
    if (currentPlan === 'LIFETIME') {
      setError("You already have Lifetime access — unlimited filings forever.");
      return;
    }

    if (plan.paymentLink) {
      localStorage.setItem('pendingPayment', JSON.stringify({
        userId: user.uid,
        userEmail: user.email,
        plan: plan.key.toLowerCase(),
        timestamp: new Date().toISOString(),
      }));

      window.location.href = `${plan.paymentLink}?prefilled_email=${encodeURIComponent(user.email)}`;
    } else {
      setError('Payment link not configured. Please contact support.');
    }
  };

  // Helper to get plan display name
  const getPlanDisplayName = (planKey) => {
    if (planKey === 'PAID') return 'Single Filing';
    if (planKey === 'LIFETIME') return 'Lifetime Unlimited';
    return '—';
  };

  if (authLoading) {
    return (
      <div className="pp-loading">
        <div className="pp-spinner" />
        <span>Loading…</span>
      </div>
    );
  }

  return (
    <div className="pp-root">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="pp-header">
        <div className="pp-eyebrow">Pricing</div>
        <h1 className="pp-title">One price. <em>No surprises.</em></h1>
        <p className="pp-sub">
          Pay once per filing across all 4 tools, or get lifetime access for unlimited filings.<br />
          No subscriptions, no renewals.
        </p>

        {/* Current plan banner */}
        {user && !planLoading && currentPlan && (
          <div className="pp-current-banner">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Current plan: <strong>{getPlanDisplayName(currentPlan)}</strong>
            <span className="pp-banner-dot">·</span>
            {usageStats?.canGenerate && usageStats?.filingsRemaining === 'Unlimited'
              ? 'Unlimited filings'
              : `${usageStats?.filingsRemaining ?? 0} filing${usageStats?.filingsRemaining === 1 ? '' : 's'} remaining`}
          </div>
        )}

        {/* Not purchased yet */}
        {user && !planLoading && !currentPlan && (
          <div className="pp-current-banner pp-banner-unpaid">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            You don't have an active plan yet. Purchase below to start filing.
          </div>
        )}

        {success && (
          <div className="pp-alert pp-alert-success">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
            {success}
          </div>
        )}

        {error && (
          <div className="pp-alert pp-alert-error">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            {error}
          </div>
        )}
      </div>

      {/* ── Plan cards ─────────────────────────────────────────────────────── */}
      <div className="pp-grid">
        {pricingPlans.map((plan) => {
          const isCurrent = !!currentPlan && currentPlan === plan.key;
          const isFeatured = plan.popular;

          return (
            <div
              key={plan.key}
              className={`pp-card ${isFeatured ? 'pp-card-featured' : ''} ${isCurrent ? 'pp-card-current' : ''}`}
            >
              <span className={`pp-badge ${plan.key === 'PAID' ? 'pp-badge-pop' : 'pp-badge-power'}`}>
                {plan.popular ? 'Most popular' : 'Best value'}
              </span>

              <div className="pp-plan-name">{plan.name}</div>
              <div className="pp-price">
                ${plan.price}
                <span className="pp-period">/ {plan.period}</span>
              </div>

              <p className="pp-plan-desc">{plan.description}</p>

              <div className="pp-pill">
                <span className="pp-pill-label">Filings</span>
                <span className="pp-pill-val">
                  {plan.isUnlimited ? 'Unlimited' : plan.filingsAllowed}
                </span>
              </div>

              <div className="pp-divider" />

              <ul className="pp-feats">
                {plan.features.map((f, i) => (
                  <li key={i} className="pp-feat">
                    <CheckIcon />
                    {f}
                  </li>
                ))}
              </ul>

              <button
                className={`pp-btn ${isFeatured ? 'pp-btn-primary' : 'pp-btn-outline'} ${isCurrent ? 'pp-btn-current' : ''}`}
                onClick={() => handleSelect(plan)}
                disabled={isCurrent || currentPlan === 'LIFETIME'}
              >
                {isCurrent ? (
                  <><CheckIcon color="var(--green)" /> Active plan</>
                ) : !user ? (
                  'Sign in to purchase'
                ) : (
                  plan.cta
                )}
              </button>
            </div>
          );
        })}
      </div>

      {/* ── Trust notes ────────────────────────────────────────────────────── */}
      <div className="pp-notes">
        <span>✓ One-time payment</span>
        <span>✓ No subscription</span>
        <span>✓ Works across all 4 tools</span>
        <span>✓ $25,000 penalty avoided</span>
      </div>

      {/* ── Usage stats ────────────────────────────────────────────────────── */}
      {user && usageStats && !planLoading && currentPlan && (
        <div className="pp-usage-section">
          <h3 className="pp-usage-title">Your filing usage</h3>
          <div className="pp-usage-grid">
            {[
              ['Plan', getPlanDisplayName(currentPlan)],
              ['Filings used', usageStats.filingsUsed ?? 0],
              ['Filings remaining', usageStats.filingsRemaining ?? 0],
              ['Total allowed', usageStats.filingsAllowed ?? '—'],
            ].map(([label, val]) => (
              <div key={label} className="pp-usage-card">
                <div className="pp-usage-label">{label}</div>
                <div className="pp-usage-val">{val}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── FAQ ────────────────────────────────────────────────────────────── */}
      <div className="pp-faq-section">
        <h3 className="pp-faq-title">Common questions</h3>
        <div className="pp-faq-list">
          {FAQS.map((f, i) => (
            <FAQItem key={i} question={f.question} answer={f.answer} />
          ))}
        </div>
      </div>

      <p className="pp-legal">
        Not legal advice. For complex tax matters, consult a qualified CPA or tax attorney.
      </p>
    </div>
  );
}